import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd, calcEst } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Textarea, EmptyState } from '../ui.jsx';
import { ContactLink } from '../widgets/ContactQuickEdit.jsx';
import { ContactPicker } from '../widgets/ContactPicker.jsx';
import { PriceBookPicker } from '../widgets/PriceBookPicker.jsx';

const STATUSES = ['draft','sent','paid','overdue'];
const ST_COLOR = { draft:G.textMuted, sent:G.cyan, paid:G.green, overdue:G.red };

export function Invoices({ toast, integrationStatus, go }) {
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [followUp, setFollowUp] = useState(null);

  const load = async () => {
    try {
      const [i, c] = await Promise.all([api('/api/invoices'), api('/api/contacts').catch(()=>[])]);
      setInvoices(i); setContacts(c);
    } catch(e) { toast(e.message,'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('tradeops:tab-focus', onFocus);
    return () => window.removeEventListener('tradeops:tab-focus', onFocus);
  }, []);

  const sendInvoice = async inv => {
    if (!confirm(`Send invoice ${inv.num} to ${inv.contact} via SMS+email? This also starts the follow-up sequence.`)) return;
    try {
      const r = await api(`/api/invoices/${inv.id}/send`, { method:'POST' });
      load();
      toast(r.paymentLink?.includes('stripe') ? 'Sent with Stripe payment link + follow-up enrolled' : 'Sent + follow-up enrolled (Stripe not connected, no pay link)', r.paymentLink?.includes('stripe')?'success':'warn');
    } catch(e) { toast(e.message,'error'); }
  };

  const sendPaymentLink = async inv => {
    if (!integrationStatus?.stripe?.connected) {
      toast('Stripe must be connected first. Go to Integrations.', 'warn');
      return;
    }
    try {
      const result = await api(`/api/integrations/stripe/payment-link/${inv.id}`, { method:'POST' });
      window.prompt('Payment link generated. Copy and send to customer:', result.url);
      load(); toast('Payment link created');
    } catch(e) { toast(e.message,'error'); }
  };

  const [payingInvoice, setPayingInvoice] = useState(null); // shows MarkPaidModal
  const markPaid = async inv => {
    if (!confirm(`Mark invoice ${inv.num} as paid?`)) return;
    try { await api(`/api/invoices/${inv.id}/mark-paid`, { method:'POST' }); load(); toast('Marked paid + thank-you sequence enrolled'); }
    catch(e) { toast(e.message,'error'); }
  };

  const remove = async inv => {
    if (!confirm('Delete this invoice?')) return;
    try { await api(`/api/invoices/${inv.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Invoices <span style={{ color:G.textMuted, fontWeight:500, fontSize:18 }}>({invoices.length})</span></h1>
      <div style={{ display:'flex', gap:8 }}>
        <Btn v="ghost" onClick={()=>window.location.href='/api/invoices/export.csv'}>↓ Export CSV</Btn>
        <Btn v="ghost" onClick={()=>go && go('migration')} title="Import invoices from a CSV / Excel export">📥 Import</Btn>
        <Btn v="primary" onClick={()=>setEditing({ status:'draft', taxRate:7, lines:[{name:'',qty:1,price:0,unit:'each'}] })}>+ New Invoice</Btn>
      </div>
    </div>

    {!integrationStatus?.stripe?.connected && <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor:G.yellow+'66', background:G.yellowLight }}>
      <strong style={{ color:G.yellow }}>Stripe not connected.</strong> <span style={{ color:G.textMuted }}>Connect Stripe in Integrations to generate payment links.</span>
    </div>}

    {loading ? <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>
    : <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>
          {['#','Contact','Service','Total','Paid','Status','Due','Actions'].map(h => <th key={h} style={styles.th}>{h}</th>)}
        </tr></thead>
        <tbody>{invoices.map(inv => {
          const { total } = calcEst(inv.lines, inv.taxRate);
          const balance = total + (inv.tip||0) - (inv.paid||0);
          return <tr key={inv.id}>
            <td style={{ ...styles.td, fontWeight:600 }}>{inv.num}</td>
            <td style={styles.td}><ContactLink id={inv.contactId}>{inv.contact}</ContactLink></td>
            <td style={styles.td}>{inv.service || '—'}</td>
            <td style={{ ...styles.td, fontWeight:600 }}>{fmtUsd(total + (inv.tip||0))}</td>
            <td style={styles.td}>{fmtUsd(inv.paid||0)}</td>
            <td style={styles.td}>
              {/* Inline status toggle — flip between draft / sent / paid / overdue without opening the edit modal */}
              <Select value={inv.status||'draft'} onChange={async e=>{
                try { await api(`/api/invoices/${inv.id}`, { method:'PATCH', body:{ status: e.target.value }}); load(); toast(`Status → ${e.target.value}`); }
                catch(err) { toast(err.message,'error'); }
              }} style={{ padding:'4px 8px', fontSize:11, fontWeight:700, color:ST_COLOR[inv.status]||G.text, background:(ST_COLOR[inv.status]||G.text)+'22', borderColor:(ST_COLOR[inv.status]||G.cardBorder)+'44', borderRadius:99, minWidth:90 }}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
            </td>
            <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{inv.due || '—'}</td>
            <td style={styles.td}>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                <Btn sz="xs" onClick={()=>setEditing(inv)}>Edit</Btn>
                {inv.status === 'draft' && <Btn sz="xs" v="primary" onClick={()=>sendInvoice(inv)}>Send</Btn>}
                {['sent','overdue'].includes(inv.status) && <Btn sz="xs" v="cyan" onClick={()=>setFollowUp(inv)}>Follow Up</Btn>}
                {inv.status !== 'paid' && integrationStatus?.stripe?.connected && <Btn sz="xs" v="primary" onClick={()=>sendPaymentLink(inv)}>Pay Link</Btn>}
                {inv.status !== 'paid' && <Btn sz="xs" v="green" onClick={()=>setPayingInvoice(inv)}>Mark Paid</Btn>}
                <Btn sz="xs" v="danger" onClick={()=>remove(inv)}>×</Btn>
              </div>
            </td>
          </tr>;
        })}</tbody>
      </table>
      {invoices.length === 0 && <EmptyState
        title="No invoices yet"
        body={integrationStatus?.stripe?.connected
          ? "Send your first invoice — customers can pay by card, Apple Pay, Google Pay, or ACH in one tap."
          : "Connect Stripe first so customers can pay in one tap. Invoices without payment links convert ~40% lower."}
        ctaLabel={integrationStatus?.stripe?.connected ? '+ New invoice' : 'Connect Stripe →'}
        onCta={()=> integrationStatus?.stripe?.connected ? setEditing({ lines:[{ name:'', qty:1, price:0, unit:'each' }] }) : (go && go('integrations'))}
      />}
    </div>}

    <InvoiceModal invoice={editing} contacts={contacts} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Invoice saved'); }}/>
    <FollowUpModal invoice={followUp} onClose={()=>setFollowUp(null)} onSent={()=>{ load(); setFollowUp(null); toast('Follow-up sent'); }}/>
    <MarkPaidModal invoice={payingInvoice} onClose={()=>setPayingInvoice(null)} onPaid={(msg)=>{ load(); setPayingInvoice(null); toast(msg); }}/>
  </div>;
}

function FollowUpModal({ invoice, onClose, onSent }) {
  const [channel, setChannel] = useState('both');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  if (!invoice) return null;
  const send = async () => {
    setBusy(true);
    try { await api(`/api/invoices/${invoice.id}/follow-up`, { method:'POST', body:{ channel, message: message||null } }); onSent(); }
    catch(e) { alert(e.message); setBusy(false); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 4px 0' }}>Send Payment Reminder</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:18 }}>To: <strong style={{ color:G.text }}>{invoice.contact}</strong> · Invoice: <strong style={{ color:G.text }}>{invoice.num}</strong> · Status: <Badge c={invoice.status==='overdue'?G.red:G.cyan} bg={(invoice.status==='overdue'?G.red:G.cyan)+'22'}>{invoice.status}</Badge></div>
    <Field label="Channel">
      <Select value={channel} onChange={e=>setChannel(e.target.value)}>
        <option value="both">SMS + Email</option>
        <option value="sms">SMS only</option>
        <option value="email">Email only</option>
      </Select>
    </Field>
    <Field label="Message (optional — leave blank for default)" hint="Default automatically references the days-late if overdue and includes the payment link.">
      <Textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Hi {first_name}, friendly reminder about invoice..."/>
    </Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={send} disabled={busy}>{busy?<Spinner/>:'Send Reminder'}</Btn>
    </div>
  </Modal>;
}

function InvoiceModal({ invoice, contacts, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(invoice || {}); }, [invoice]);
  if (!invoice) return null;
  const isNew = !form.id;
  const lines = form.lines || [];
  const { sub, tax, total } = calcEst(lines, form.taxRate);

  const updateLine = (i, k, v) => {
    const nl = lines.slice();
    nl[i] = { ...nl[i], [k]: k==='name'||k==='unit' ? v : (Number(v)||0) };
    setForm({ ...form, lines: nl });
  };
  const addLine = () => setForm({ ...form, lines: [...lines, { name:'', qty:1, price:0, unit:'each' }] });
  const delLine = i => setForm({ ...form, lines: lines.filter((_,j)=>j!==i) });

  const save = async () => {
    try {
      if (isNew) await api('/api/invoices', { method:'POST', body: form });
      else await api(`/api/invoices/${form.id}`, { method:'PATCH', body: form });
      onSaved();
    } catch(e) { alert(e.message); }
  };

  return <Modal open onClose={onClose} width={840}>
    <h2 style={{ margin:'0 0 18px 0' }}>{isNew ? 'New Invoice' : `${form.num}`}</h2>
    {/* Customer summary — verify the data going on the customer-facing invoice */}
    {form.contactId && (() => {
      const picked = contacts.find(x => x.id === form.contactId);
      if (!picked) return null;
      return <div style={{ ...styles.card, padding:'10px 14px', marginBottom:14, background:G.input, fontSize:12 }}>
        <div style={{ fontWeight:700, fontSize:13, marginBottom:4 }}>Bill To</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:6, color:G.textMuted }}>
          <div><strong style={{ color:G.text }}>{picked.firstName} {picked.lastName}</strong></div>
          {picked.phone && <div>{picked.phone}</div>}
          {picked.email && <div>{picked.email}</div>}
          {picked.address && <div>{[picked.address, picked.city, picked.state, picked.zip].filter(Boolean).join(', ')}</div>}
        </div>
      </div>;
    })()}
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Contact">
        <ContactPicker
          value={form.contactId}
          onChange={(c) => setForm({ ...form, contactId: c.id, contact: c.id ? `${c.firstName||''} ${c.lastName||''}`.trim() : '' })}
        />
      </Field>
      <Field label="Service"><Input value={form.service||''} onChange={e=>setForm({...form,service:e.target.value})} placeholder="AC Replacement"/></Field>
      <Field label="Tax Rate %"><Input type="number" step="0.1" value={form.taxRate||7} onChange={e=>setForm({...form,taxRate:Number(e.target.value)})}/></Field>
      <Field label="Due Date"><Input type="date" value={form.due||''} onChange={e=>setForm({...form,due:e.target.value})}/></Field>
      <Field label="Status">
        <Select value={form.status||'draft'} onChange={e=>setForm({...form,status:e.target.value})}>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </Select>
      </Field>
      <Field label="Tip"><Input type="number" step="0.01" value={form.tip||0} onChange={e=>setForm({...form,tip:Number(e.target.value)})}/></Field>
    </div>

    <div style={{ marginTop:8 }}>
      <div style={{ fontSize:12, fontWeight:600, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Line Items</div>
      <div style={{ ...styles.card, padding:12 }}>
        <table style={{ width:'100%' }}>
          <thead><tr style={{ fontSize:11, color:G.textMuted, textAlign:'left' }}>
            <th style={{ paddingBottom:6 }}>Description</th>
            <th style={{ paddingBottom:6, width:80 }}>Qty</th>
            <th style={{ paddingBottom:6, width:110 }}>Price</th>
            <th style={{ paddingBottom:6, width:80 }}>Unit</th>
            <th style={{ paddingBottom:6, width:110 }}>Total</th>
            <th style={{ paddingBottom:6, width:30 }}></th>
          </tr></thead>
          <tbody>{lines.map((l, i) => (
            <tr key={i}>
              <td>
                <PriceBookPicker
                  value={l.name}
                  onPick={(picked) => {
                    if (picked.price != null) {
                      const nl = [...lines];
                      nl[i] = { ...l, name: picked.name, price: picked.price, qty: picked.qty || l.qty || 1, unit: picked.unit || l.unit || 'each' };
                      setForm({ ...form, lines: nl });
                    } else {
                      updateLine(i, 'name', picked.name);
                    }
                  }}
                />
              </td>
              <td><Input type="number" value={l.qty||0} onChange={e=>updateLine(i,'qty',e.target.value)} /></td>
              <td><Input type="number" step="0.01" value={l.price||0} onChange={e=>updateLine(i,'price',e.target.value)} /></td>
              <td><Input value={l.unit||'each'} onChange={e=>updateLine(i,'unit',e.target.value)} /></td>
              <td style={{ padding:'0 8px', fontWeight:600 }}>{fmtUsd((Number(l.qty)||0)*(Number(l.price)||0))}</td>
              <td><Btn sz="xs" v="danger" onClick={()=>delLine(i)}>×</Btn></td>
            </tr>
          ))}</tbody>
        </table>
        <Btn sz="sm" onClick={addLine} style={{ marginTop:8 }}>+ Add Line</Btn>
      </div>
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:30, marginTop:14 }}>
      <div style={{ textAlign:'right' }}>
        <div style={{ color:G.textMuted, marginBottom:4 }}>Subtotal: <strong style={{ color:G.text }}>{fmtUsd(sub)}</strong></div>
        <div style={{ color:G.textMuted, marginBottom:4 }}>Tax: <strong style={{ color:G.text }}>{fmtUsd(tax)}</strong></div>
        {form.tip > 0 && <div style={{ color:G.textMuted, marginBottom:4 }}>Tip: <strong style={{ color:G.text }}>{fmtUsd(form.tip)}</strong></div>}
        <div style={{ fontSize:18, fontWeight:800, color:G.green }}>Total: {fmtUsd(sub + tax + (form.tip||0))}</div>
        {(form.paid||0) > 0 && <div style={{ color:G.textMuted, fontSize:13, marginTop:4 }}>Paid: {fmtUsd(form.paid)}</div>}
      </div>
    </div>

    <Field label="Notes"><Textarea value={form.notes||''} onChange={e=>setForm({...form,notes:e.target.value})}/></Field>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

// Mark-Paid modal — captures payment method (check / cash / ACH / card / other),
// optional check number, and an optional partial payment amount.
function MarkPaidModal({ invoice, onClose, onPaid }) {
  const [method, setMethod] = React.useState('check');
  const [checkNumber, setCheckNumber] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (invoice) {
      const { total } = calcEst(invoice.lines, invoice.taxRate);
      setAmount(String(total + (invoice.tip||0) - (invoice.paid||0)));
      setMethod('check'); setCheckNumber(''); setNote('');
    }
  }, [invoice]);
  if (!invoice) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api(`/api/invoices/${invoice.id}/mark-paid`, { method:'POST', body:{
        method, checkNumber: method === 'check' ? (checkNumber || null) : null,
        amount: amount ? Number(amount) : undefined,
        note: note || null,
      }});
      onPaid(`✓ Marked paid via ${method}${checkNumber ? ` (#${checkNumber})` : ''}`);
    } catch(e) { alert(e.message); } finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h3 style={{ margin:'0 0 14px 0' }}>Record Payment — {invoice.num}</h3>
    <Field label="Payment Method">
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6 }}>
        {[
          { v:'check', label:'📝 Check' },
          { v:'cash', label:'💵 Cash' },
          { v:'ach', label:'🏦 ACH' },
          { v:'card', label:'Card' },
          { v:'other', label:'…Other' },
        ].map(m => (
          <button key={m.v} onClick={()=>setMethod(m.v)} style={{
            padding:'10px 6px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer',
            border: `2px solid ${method===m.v ? G.purple : G.cardBorder}`,
            background: method===m.v ? G.purpleDim : G.input, color: G.text,
          }}>{m.label}</button>
        ))}
      </div>
    </Field>
    {method === 'check' && <Field label="Check Number (optional)">
      <Input value={checkNumber} onChange={e=>setCheckNumber(e.target.value)} placeholder="e.g. 1234"/>
    </Field>}
    <Field label="Amount Received">
      <Input type="number" step="0.01" value={amount} onChange={e=>setAmount(e.target.value)}/>
    </Field>
    <div style={{ fontSize:11, color:G.textMuted, marginTop:-10, marginBottom:14 }}>
      Less than the full amount = partial payment, invoice stays open.
    </div>
    <Field label="Note (optional)">
      <Input value={note} onChange={e=>setNote(e.target.value)} placeholder="Anything to remember about this payment"/>
    </Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={submit} disabled={busy}>{busy?<Spinner sz={14}/>:'✓ Record Payment'}</Btn>
    </div>
  </Modal>;
}
